import { Frame, Head, Label, Node, Pill, fx } from './frame';
import { useStrings } from '../../../state/useLocale';
import type { FigureProps } from './index';

/**
 * The six figures about the shape of history.
 *
 * They are drawn as one family because they are variations on one sentence: a
 * commit is a snapshot with a parent, and every command in this group either
 * adds one, copies one, joins two, or moves a name. The lanes, the node sizes
 * and the pill treatment are identical across all six, so the difference
 * between a merge and a rebase is the only thing that changes between the
 * pictures.
 *
 * Where a commit is genuinely new it arrives the way the live graph makes one
 * arrive — the node drops, then its edge draws to its parent, then the ref
 * slides across (motion.css, "A commit lands"). Where a commit is a copy, the
 * original is left drawn as an unreachable one rather than deleted, because
 * that is what actually happened to it.
 *
 * Every hash, every ref pill and the word `squash` are the machine's and stay
 * in Latin; the caption under each drawing is the author's sentence and comes
 * from the catalogue. An Arabic caption still says `commit`, because that is
 * the word on the keyboard (docs/i18n.md §5). See frame.tsx, `Label`.
 */

/* ── commit-chain ────────────────────────────────────────────────────────────
   Four snapshots and three arrows, and every arrow points backwards. A commit
   knows its parent; a parent knows nothing about its children, which is why
   adding the fourth commit changes none of the first three — they do not move
   in the animation, and that stillness is the claim about immutability. */

export function CommitChainFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;
  const older = [
    { hash: '9f8e7d6', x: 44 },
    { hash: 'a1b2c3d', x: 118 },
    { hash: '4c5d6e7', x: 192 },
  ];

  return (
    <Frame name="commit-chain" active={active}>
      <g className="fx-in" style={fx(0)}>
        <path className="edge" d="M111 70 H57 M185 70 H131" />
        <Head x={53} y={70} dir="left" />
        <Head x={127} y={70} dir="left" />
        {older.map((commit) => (
          <Node key={commit.hash} x={commit.x} y={70} />
        ))}
      </g>

      {older.map((commit, i) => (
        <text key={commit.hash} className="lbl-dim fx-in" style={fx(140 + i * 70)} x={commit.x} y={94} textAnchor="middle">
          {commit.hash}
        </text>
      ))}

      {/* The new commit, and the arrow it points back with. */}
      <Node className="fx-drop" style={fx(520)} x={266} y={70} kind="signal" />
      <path className="edge fx-draw" style={fx(720, { '--t': '180ms', '--len': 70 })} d="M259 70 H205" />
      <Head className="fx-in" style={fx(860)} x={201} y={70} dir="left" />
      <text className="lbl-ink fx-in" style={fx(640)} x={266} y={94} textAnchor="middle">
        b7e1f20
      </text>

      <Pill className="fx-slide" style={fx(920, { '--dx': '-74px' })} x={248} y={36} text="HEAD" kind="head" />

      <Label className="lbl-dim fx-in" style={fx(1080)} x={160} y={126}>
        {f.pointsAtParent}
      </Label>
    </Frame>
  );
}

/* ── branch-pointer ──────────────────────────────────────────────────────────
   The commits are deliberately inert. Everything that moves is in one group —
   the green pointer, the branch name it carries, and HEAD hanging off the
   branch rather than off the commit — so a checkout or a commit reads as a
   label travelling along a history that did not change. */

export function BranchPointerFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;
  const commits = [52, 126, 200, 274];

  return (
    <Frame name="branch-pointer" active={active}>
      <g className="fx-in" style={fx(0)}>
        <path className="edge" d="M52 40 H274" />
        {commits.map((x) => (
          <Node key={x} x={x} y={40} />
        ))}
      </g>

      <g className="fx-slide" style={fx(480, { '--t': '420ms', '--dx': '-74px' })}>
        <circle className="sig" cx={274} cy={40} r={3} />
        <path className="wire sig" d="M274 46 V60" />
        <Pill x={255} y={60} text="main" kind="head" />
        <path className="wire" d="M274 98 V84" />
        <Head x={274} y={80} dir="up" />
        <text x={274} y={110} textAnchor="middle">
          HEAD
        </text>
      </g>

      <Label className="lbl-dim fx-in" style={fx(1000)} x={160} y={134}>
        {f.nameMoves}
      </Label>
    </Frame>
  );
}

/* ── merge ───────────────────────────────────────────────────────────────────
   The only commit in the product with two edges leaving it, drawn so that the
   two edges leave at the same moment. Nothing on either branch is rewritten;
   the merge commit simply has two parents, and the branch label follows it. */

export function MergeFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;

  return (
    <Frame name="merge" active={active}>
      <g className="fx-in" style={fx(0)}>
        <path className="edge" d="M40 44 H152" />
        <path className="edge" d="M96 96 C68 96, 68 44, 40 44" />
        <path className="edge" d="M96 96 H152" />
        <Node x={40} y={44} />
        <Node x={96} y={44} />
        <Node x={152} y={44} />
        <Node x={96} y={96} kind="dim" />
        <Node x={152} y={96} kind="dim" />
      </g>

      <Pill className="fx-in" style={fx(260)} x={125} y={110} text="feature" />

      <Node className="fx-drop" style={fx(560)} x={232} y={44} kind="signal" />
      <path className="edge fx-draw" style={fx(740, { '--t': '220ms', '--len': 92 })} d="M232 44 H152" />
      <path
        className="edge fx-draw"
        style={fx(760, { '--t': '240ms', '--len': 130 })}
        d="M232 44 C204 44, 180 96, 152 96"
      />

      <Pill className="fx-slide" style={fx(1000, { '--dx': '-80px' })} x={214} y={18} text="main" kind="head" />

      <Label className="lbl-dim fx-in" style={fx(1080)} x={160} y={140}>
        {f.twoParents}
      </Label>
    </Frame>
  );
}

/* ── rebase ──────────────────────────────────────────────────────────────────
   Two commits replayed, one at a time, onto a new base. The primes on the
   copied hashes are the whole argument: these are new objects with new
   parents, not the same commits relocated. The originals stay on the lower
   lane and fade into the unreachable drawing, because that is exactly what
   they become. */

export function RebaseFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;

  return (
    <Frame name="rebase" active={active}>
      <g className="fx-in" style={fx(0)}>
        <path className="edge" d="M36 42 H148" />
        <Node x={36} y={42} />
        <Node x={92} y={42} />
        <Node x={148} y={42} />
      </g>

      {/* The originals, in one group so the last beat can recede all of it. */}
      <g className="fx-dim" style={fx(1000, { '--t': '320ms' })} opacity={0.55}>
        <g className="fx-in" style={fx(120)}>
          <path className="edge ghost" d="M92 100 C64 100, 64 42, 36 42" />
          <path className="edge ghost" d="M92 100 H148" />
          <Node x={92} y={100} kind="ghost" r={5} />
          <Node x={148} y={100} kind="ghost" r={5} />
          <text className="lbl-ghost" x={92} y={122} textAnchor="middle">
            a1b
          </text>
          <text className="lbl-ghost" x={148} y={122} textAnchor="middle">
            c2d
          </text>
        </g>
      </g>

      <path className="wire ghost fx-in" style={fx(420)} d="M92 92 C130 88, 168 60, 202 50" />
      <Node className="fx-drop" style={fx(600)} x={208} y={42} kind="signal" />
      <path className="edge fx-draw" style={fx(760, { '--t': '180ms', '--len': 70 })} d="M148 42 H208" />
      <text className="lbl-ink fx-in" style={fx(700)} x={208} y={24} textAnchor="middle">
        a1b&#39;
      </text>

      <path className="wire ghost fx-in" style={fx(760)} d="M148 92 C180 88, 210 62, 254 50" />
      <Node className="fx-drop" style={fx(900)} x={262} y={42} kind="signal" />
      <path className="edge fx-draw" style={fx(1020, { '--t': '160ms', '--len': 66 })} d="M208 42 H262" />
      <text className="lbl-ink fx-in" style={fx(960)} x={262} y={24} textAnchor="middle">
        c2d&#39;
      </text>

      <Label className="lbl-dim fx-in" style={fx(1120)} x={160} y={140}>
        {f.replayed}
      </Label>
    </Frame>
  );
}

/* ── cherry-pick ─────────────────────────────────────────────────────────────
   The same drawing as a rebase with one commit instead of two, and with the
   original left alone: a cherry-pick copies, it does not move. The hint arc is
   the copy; the solid edge is the new commit's actual parent link, which
   points at main's tip and not at where the change came from. */

export function CherryPickFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;

  return (
    <Frame name="cherry-pick" active={active}>
      <g className="fx-in" style={fx(0)}>
        <text className="lbl-dim" x={14} y={52}>
          main
        </text>
        <text className="lbl-dim" x={14} y={104}>
          feature
        </text>
        <path className="edge" d="M66 48 H174" />
        <path className="edge" d="M120 100 C92 100, 92 48, 66 48" />
        <path className="edge" d="M120 100 H228" />
        <Node x={66} y={48} />
        <Node x={120} y={48} />
        <Node x={174} y={48} />
        <Node x={120} y={100} kind="dim" />
        <Node x={174} y={100} kind="dim" />
        <Node x={228} y={100} kind="dim" />
      </g>

      <text className="lbl-dim fx-in" style={fx(300)} x={174} y={122} textAnchor="middle">
        c2d
      </text>

      <g className="fx-in" style={fx(520, { '--t': '300ms' })}>
        <path className="wire ghost" d="M174 92 C204 90, 232 84, 238 60" />
        <Head className="ghost" x={238} y={56} dir="up" />
      </g>

      <Node className="fx-drop" style={fx(800)} x={238} y={48} kind="signal" />
      <path className="edge fx-draw" style={fx(960, { '--t': '180ms', '--len': 74 })} d="M174 48 H238" />
      <text className="lbl-ink fx-in" style={fx(900)} x={238} y={30} textAnchor="middle">
        c2d&#39;
      </text>

      <Label className="lbl-dim fx-in" style={fx(1120)} x={160} y={140}>
        {f.sameChangeNewCommit}
      </Label>
    </Frame>
  );
}

/* ── squash ──────────────────────────────────────────────────────────────────
   Three commits in, one commit out. The brace counts what went in so the
   single node on the right is read as a total rather than as a fourth commit,
   and the caption is careful about which half survives: the work does, the
   three commits do not. */

export function SquashFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;
  const before = [64, 104, 144];

  return (
    <Frame name="squash" active={active}>
      <g className="fx-in" style={fx(0)}>
        <path className="edge" d="M64 52 H144" />
        {before.map((x) => (
          <Node key={x} x={x} y={52} />
        ))}
      </g>

      <path className="wire fx-draw" style={fx(320, { '--t': '280ms', '--len': 110 })} d="M64 64 V72 H144 V64" />
      <path className="wire fx-in" style={fx(560)} d="M104 72 V80" />
      <Label className="fx-in" style={fx(560)} x={104} y={94}>
        {f.threeCommits}
      </Label>

      <g className="fx-in" style={fx(680)}>
        <text className="lbl-dim" x={182} y={40} textAnchor="middle">
          squash
        </text>
        <path className="wire bright" d="M166 52 H192" />
        <Head className="bright" x={198} y={52} dir="right" />
      </g>

      <Node className="fx-pop" style={fx(880)} x={248} y={52} kind="signal" />
      <g className="fx-in" style={fx(1000)}>
        <path className="wire" d="M248 64 V80" />
        <Label x={248} y={94}>
          {f.oneCommit}
        </Label>
      </g>

      <Label className="lbl-dim fx-in" style={fx(1120)} x={160} y={126}>
        {f.workSurvives}
      </Label>
    </Frame>
  );
}
